import { useTranslation } from 'next-i18next';
import { CSSProperties, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import toast from 'react-hot-toast';
import { EventInterface, PageData } from '../../../../../interfaces/interfaces';
import { StageComponentInterface } from '../../../../../interfaces/pagebuilder';
import { setBroadcast } from '../../../../../reduxToolkit/slices/commonSlice';
import { useSelector } from '../../../../../reduxToolkit/typedRedux';
import { customAxios } from '../../../../../services/backend';
import { createDisplayLink } from '../../../../../services/helper';
import VyReactPlayer from '../../../../common/VyReactPlayer';
import { isExceedingOrgBudget } from '../../../../../services/services';

const VystemStream = ({
  stageSettings,
  iFrameStyle,
  uiComponent,
  playerStyle,
  isFullScreen = false,
  openCustomFullScreen = false,
  currentTime,
  isPlaying,
  onCustomFullScreen,
  onClickResize,
}: {
  stageSettings: PageData['stageSettings'];
  iFrameStyle?: CSSProperties;
  uiComponent?: StageComponentInterface;
  playerStyle?: CSSProperties;
  onCustomFullScreen?: (time?: number, isPlaying?: boolean) => void;
  onClickResize?: any;
  isFullScreen?: boolean;
  openCustomFullScreen?: boolean;
  currentTime?: number;
  isPlaying?: boolean;
}) => {
  const { t } = useTranslation('toasts');
  const liveStream = useSelector((state) => state.common.livestreams.find((ls) => ls._id === stageSettings?.vystemstream?._id));
  const dispatch = useDispatch();

  const [isReady, setIsReady] = useState<boolean>(false);
  const [alreadyBroadcasted, setAlreadyBroadcasted] = useState(liveStream?.isBroadcasting);
  const [canPlay, setCanPlay] = useState<boolean | undefined>();

  async function isStreamBudgetAvailable() {
    if (!liveStream) return;
    try {
      await isExceedingOrgBudget((liveStream?.event as EventInterface).organization.toString(), 'stream');
      setCanPlay(true);
    } catch (err) {
      setCanPlay(false);
      console.error(err);
    }
  }

  useEffect(() => {
    isStreamBudgetAvailable();
  }, []);

  useEffect(() => {
    isStreamBudgetAvailable();
  }, [liveStream]);

  useEffect(() => {
    if (liveStream?.isBroadcasting === undefined) return;
    setAlreadyBroadcasted(liveStream.isBroadcasting);

    if (liveStream?.isBroadcasting && alreadyBroadcasted === false) {
      dispatch(
        setBroadcast({
          _id: `livestream-${liveStream._id}`,
          message: `
        <div style='width:100%;padding:4px;display:flex;flex-direction:column;align-items:center;'>
          <b style='margin:0 2px;'>${t('event:livestream-broadcast-modal-title')}</b>
          <p style='margin:0 2px;'>${t('event:livestream-broadcast-modal-content')}</p>
          </div>
        `,
          type: 'modal',
          open: true,
        })
      );
      setAlreadyBroadcasted(true);
    }
  }, [liveStream?.isBroadcasting]);

  async function track(event: string, data?: any) {
    try {
      if (stageSettings?.vystemstream?._id) {
        const eventId = typeof stageSettings?.vystemstream?.event === 'string' ? stageSettings?.vystemstream?.event : stageSettings?.vystemstream?.event?._id;
        if (!eventId) return;
        customAxios.post('/tracking/track', { event: event, eventid: eventId, args: { streamId: stageSettings?.vystemstream?._id, ...data } }).catch((err) => {
          console.error(err);
        });
      }
    } catch (err) {
      console.error(`Err > ${err}`);
    }
  }

  if (canPlay === false) {
    return <p>{t('event:cannot-play-due-to-streaming-budget')}</p>;
  }

  if (stageSettings?.vystemstream?.hls) {
    return (
      <VyReactPlayer
        props={{
          style: playerStyle ?? { backgroundColor: 'black', width: '100%', maxWidth: '100% !important' },
          width: '100%',
          height: '100%',
          controls: true,
          playing: isPlaying ?? stageSettings.autoplay ?? false,
          light: stageSettings?.vystemstream?.thumbnail ? createDisplayLink(stageSettings.vystemstream.thumbnail) : undefined,
          url: stageSettings?.vystemstream?.hls,
          onReady: () => {
            if (isReady === false) setIsReady(true);
          },
          onPlay: () => {
            onClickResize?.();
            try {
              if (!isReady && stageSettings?.vystemstream?.isBroadcasting === false && stageSettings?.vystemstream?.liveStreamType === 'mux') {
                toast(t('toasts:stream-not-live'));
              }

              track('LIVESTREAM_PLAY');
            } catch (err) {
              console.error(`Err > ${err}`);
            }
          },
          onPause: () => {
            onClickResize?.();
            track('LIVESTREAM_PAUSE');
          },
        }}
        stageSettings={stageSettings}
        uiComponent={uiComponent}
        playerStyle={playerStyle}
        onCustomFullScreen={onCustomFullScreen}
        isFullScreen={isFullScreen}
        openCustomFullScreen={openCustomFullScreen}
        currentTime={currentTime}
      />
    );
  }

  return (
    <div
      onClick={() => onClickResize?.()}
      className="transition-transform "
      style={playerStyle ? {} : { position: 'relative', overflow: 'hidden', width: '100%', paddingTop: '54.25%' }}>
      <iframe style={iFrameStyle} src={stageSettings.livestream ?? undefined} width="100%" height="100%" frameBorder={0} scrolling="no" allowFullScreen />
    </div>
  );
};
export default VystemStream;
